import {
  Button,
  FieldWrapper,
  Icons,
  Label,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  Textarea,
  useToggle,
} from '@commenda/kakashi';
import { TLLMStepDefinition } from '@commenda/types';
import { ExpressionType, TExpr, TMapping } from '@commenda/types/src/workflow/dsl';
import { useState } from 'react';
import { useWorkflow } from '../../context/WorkflowContext';
import { TSelectedEntity } from '../../types/node';
import { ExprEditor } from '../editors/ExperssionEditor';
import { MappingModal } from '../modals/AddEditMappingModal';

const ANTHROPIC_MODELS = [
  'claude-opus-4-6',
  'claude-opus-4-5-20251101',
  'claude-opus-4-5',
  'claude-3-7-sonnet-latest',
  'claude-3-7-sonnet-20250219',
  'claude-3-5-haiku-latest',
  'claude-3-5-haiku-20241022',
  'claude-haiku-4-5',
  'claude-haiku-4-5-20251001',
  'claude-sonnet-4-20250514',
  'claude-sonnet-4-0',
  'claude-4-sonnet-20250514',
  'claude-sonnet-4-5',
  'claude-sonnet-4-5-20250929',
  'claude-opus-4-0',
  'claude-opus-4-20250514',
  'claude-4-opus-20250514',
  'claude-opus-4-1-20250805',
  'claude-3-opus-latest',
  'claude-3-opus-20240229',
  'claude-3-haiku-20240307',
] as const;

const OPENAI_MODELS = [
  'gpt-5.2',
  'gpt-5.2-2025-12-11',
  'gpt-5.2-chat-latest',
  'gpt-5.2-pro',
  'gpt-5.2-pro-2025-12-11',
  'gpt-5.1',
  'gpt-5.1-2025-11-13',
  'gpt-5.1-codex',
  'gpt-5.1-mini',
  'gpt-5.1-chat-latest',
  'gpt-5',
  'gpt-5-mini',
  'gpt-5-nano',
  'gpt-5-2025-08-07',
  'gpt-5-mini-2025-08-07',
  'gpt-5-nano-2025-08-07',
  'gpt-5-chat-latest',
  'gpt-4.1',
  'gpt-4.1-mini',
  'gpt-4.1-nano',
  'gpt-4.1-2025-04-14',
  'gpt-4.1-mini-2025-04-14',
  'gpt-4.1-nano-2025-04-14',
  'o4-mini',
  'o4-mini-2025-04-16',
  'o3',
  'o3-2025-04-16',
  'o3-mini',
  'o3-mini-2025-01-31',
  'o1',
  'o1-2024-12-17',
  'o1-preview',
  'o1-preview-2024-09-12',
  'o1-mini',
  'o1-mini-2024-09-12',
  'gpt-4o',
  'gpt-4o-2024-11-20',
  'gpt-4o-2024-08-06',
  'gpt-4o-2024-05-13',
  'gpt-4o-audio-preview',
  'gpt-4o-audio-preview-2024-10-01',
  'gpt-4o-audio-preview-2024-12-17',
  'gpt-4o-audio-preview-2025-06-03',
  'gpt-4o-mini-audio-preview',
  'gpt-4o-mini-audio-preview-2024-12-17',
  'gpt-4o-search-preview',
  'gpt-4o-mini-search-preview',
  'gpt-4o-search-preview-2025-03-11',
  'gpt-4o-mini-search-preview-2025-03-11',
  'chatgpt-4o-latest',
  'codex-mini-latest',
  'gpt-4o-mini',
  'gpt-4o-mini-2024-07-18',
  'gpt-4-turbo',
  'gpt-4-turbo-2024-04-09',
  'gpt-4-0125-preview',
  'gpt-4-turbo-preview',
  'gpt-4-1106-preview',
  'gpt-4-vision-preview',
  'gpt-4',
  'gpt-4-0314',
  'gpt-4-0613',
  'gpt-4-32k',
  'gpt-4-32k-0314',
  'gpt-4-32k-0613',
  'gpt-3.5-turbo',
  'gpt-3.5-turbo-16k',
  'gpt-3.5-turbo-0301',
  'gpt-3.5-turbo-0613',
  'gpt-3.5-turbo-1106',
  'gpt-3.5-turbo-0125',
  'gpt-3.5-turbo-16k-0613',
] as const;

const MODEL_OPTIONS: Record<string, readonly string[]> = {
  anthropic: ANTHROPIC_MODELS,
  openai: OPENAI_MODELS,
};

type PromptMode = 'text' | 'expression';

const resolvePromptMode = (
  value: string | TExpr | undefined
): PromptMode => {
  if (!value) return 'text';
  if (typeof value === 'string') return 'text';
  return 'expression';
};

const resolvePromptText = (value: string | TExpr | undefined): string => {
  if (!value) return '';
  if (typeof value === 'string') return value;
  return value.src;
};

export const LLMNodePropertyPanel = ({
  selectedEntity,
}: {
  selectedEntity: TSelectedEntity;
}) => {
  const { workflow, setWorkflow } = useWorkflow();

  const selectedStep = workflow?.steps.find(
    (step) => step.id === selectedEntity.id
  ) as TLLMStepDefinition | undefined;

  const [systemPromptMode, setSystemPromptMode] = useState<PromptMode>(
    resolvePromptMode(selectedStep?.llm?.systemPrompt)
  );
  const [userPromptMode, setUserPromptMode] = useState<PromptMode>(
    resolvePromptMode(selectedStep?.llm?.userPrompt)
  );

  const [mappingToEdit, setMappingToEdit] = useState<TMapping | null>(null);
  const addEditMappingModalVisibility = useToggle(false);

  if (!selectedStep) return <div>Step not found</div>;

  const llmConfig = selectedStep.llm ?? {
    provider: '',
    modelId: '',
    userPrompt: '',
  };

  const updateLLMConfig = (updates: Partial<TLLMStepDefinition['llm']>) => {
    setWorkflow({
      ...workflow,
      steps: workflow?.steps.map((step) =>
        step.id === selectedEntity.id
          ? { ...step, llm: { ...llmConfig, ...updates } }
          : step
      ),
    });
  };

  const updateStep = (updates: Partial<TLLMStepDefinition>) => {
    setWorkflow({
      ...workflow,
      steps: workflow?.steps.map((step) =>
        step.id === selectedEntity.id ? { ...step, ...updates } : step
      ),
    });
  };

  const onSaveContextProjectionMapping = (mapping: TMapping) => {
    const existing = selectedStep.contextProjection?.find(
      (m) => m.id === mapping.id
    );
    if (existing) {
      updateStep({
        contextProjection: selectedStep.contextProjection?.map((m) =>
          m.id === mapping.id ? mapping : m
        ),
      });
    } else {
      updateStep({
        contextProjection: [
          ...(selectedStep.contextProjection || []),
          mapping,
        ],
      });
    }
  };

  return (
    <div className="flex flex-col gap-4">
      <FieldWrapper>
        <Label>Provider</Label>
        <Select
          value={llmConfig.provider || ''}
          onValueChange={(value) =>
            updateLLMConfig({ provider: value, modelId: '' })
          }
        >
          <SelectTrigger>
            <SelectValue placeholder="Select a provider" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="openai">OpenAI</SelectItem>
            <SelectItem value="anthropic">Anthropic</SelectItem>
          </SelectContent>
        </Select>
      </FieldWrapper>

      <FieldWrapper>
        <Label>Model ID</Label>
        <Select
          value={llmConfig.modelId || ''}
          onValueChange={(value) => updateLLMConfig({ modelId: value })}
        >
          <SelectTrigger>
            <SelectValue placeholder="Select a model" />
          </SelectTrigger>
          <SelectContent>
            {(MODEL_OPTIONS[llmConfig.provider] ?? []).map((model) => (
              <SelectItem key={model} value={model}>
                {model}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </FieldWrapper>

      <FieldWrapper>
        <Label className="flex justify-between items-center">
          <span>System Prompt</span>
          <Select
            value={systemPromptMode}
            onValueChange={(value: PromptMode) => {
              setSystemPromptMode(value);
              if (value === 'text') {
                updateLLMConfig({
                  systemPrompt: resolvePromptText(llmConfig.systemPrompt),
                });
              } else {
                const text = resolvePromptText(llmConfig.systemPrompt);
                updateLLMConfig({
                  systemPrompt: { type: ExpressionType.JSONATA, src: text },
                });
              }
            }}
          >
            <SelectTrigger className="w-auto">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="text">Text</SelectItem>
              <SelectItem value="expression">Expression</SelectItem>
            </SelectContent>
          </Select>
        </Label>
        {systemPromptMode === 'text' ? (
          <Textarea
            placeholder="You are a helpful assistant..."
            value={
              typeof llmConfig.systemPrompt === 'string'
                ? llmConfig.systemPrompt
                : ''
            }
            onChange={(e) =>
              updateLLMConfig({ systemPrompt: e.target.value })
            }
            rows={3}
          />
        ) : (
          <ExprEditor
            value={
              typeof llmConfig.systemPrompt === 'object' &&
              llmConfig.systemPrompt !== null
                ? (llmConfig.systemPrompt as TExpr)
                : { type: ExpressionType.JSONATA, src: '' }
            }
            onChange={(value) => updateLLMConfig({ systemPrompt: value })}
            showExpressionType={false}
          />
        )}
      </FieldWrapper>

      <FieldWrapper>
        <Label className="flex justify-between items-center">
          <span>User Prompt</span>
          <Select
            value={userPromptMode}
            onValueChange={(value: PromptMode) => {
              setUserPromptMode(value);
              if (value === 'text') {
                updateLLMConfig({
                  userPrompt: resolvePromptText(llmConfig.userPrompt),
                });
              } else {
                const text = resolvePromptText(llmConfig.userPrompt);
                updateLLMConfig({
                  userPrompt: { type: ExpressionType.JSONATA, src: text },
                });
              }
            }}
          >
            <SelectTrigger className="w-auto">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="text">Text</SelectItem>
              <SelectItem value="expression">Expression</SelectItem>
            </SelectContent>
          </Select>
        </Label>
        {userPromptMode === 'text' ? (
          <Textarea
            placeholder="Enter the user prompt..."
            value={
              typeof llmConfig.userPrompt === 'string'
                ? llmConfig.userPrompt
                : ''
            }
            onChange={(e) =>
              updateLLMConfig({ userPrompt: e.target.value })
            }
            rows={4}
          />
        ) : (
          <ExprEditor
            value={
              typeof llmConfig.userPrompt === 'object' &&
              llmConfig.userPrompt !== null
                ? (llmConfig.userPrompt as TExpr)
                : { type: ExpressionType.JSONATA, src: '' }
            }
            onChange={(value) => updateLLMConfig({ userPrompt: value })}
            showExpressionType={false}
          />
        )}
      </FieldWrapper>

      <FieldWrapper>
        <Label className="flex justify-between items-center">
          <span className="grow">Context Projection</span>
          <Button
            size="sm"
            onClick={() => {
              setMappingToEdit(null);
              addEditMappingModalVisibility.show();
            }}
          >
            <Icons.Plus className="size-4" />
          </Button>
        </Label>
        <div className="flex flex-col gap-2">
          {selectedStep.contextProjection?.map((mapping) => (
            <div
              key={mapping.id}
              className="flex items-center justify-between gap-2"
            >
              <span>{mapping.targetPath}</span>
              <div className="flex items-center gap-2">
                <Icons.Edit01
                  className="size-4"
                  onClick={() => {
                    setMappingToEdit(mapping);
                    addEditMappingModalVisibility.show();
                  }}
                />
                <Icons.Trash02
                  className="size-4"
                  onClick={() => {
                    updateStep({
                      contextProjection:
                        selectedStep.contextProjection?.filter(
                          (m) => m.id !== mapping.id
                        ),
                    });
                  }}
                />
              </div>
            </div>
          ))}
        </div>
      </FieldWrapper>

      <MappingModal
        isOpen={addEditMappingModalVisibility.visible}
        onClose={() => {
          addEditMappingModalVisibility.hide();
          setMappingToEdit(null);
        }}
        mapping={mappingToEdit}
        onMappingChange={onSaveContextProjectionMapping}
        workflowDefinition={workflow}
        currentStepId={selectedEntity.id}
      />
    </div>
  );
};
